/**
 * M28 task-05 (ADM-12/14; COP-117 D-14) — the document detail panel. Renders
 * the LLM summary, the processing `readiness` pin (ready / searchable / status
 * / failureReason), and the tuple-derived `access` summary (owner, shared-with-
 * track, shared-with-everyone, viewer count) from GET /documents/:id
 * (DocumentDetail). The "Generate summary" action calls POST /documents/:id/
 * summary and is shown to admins OR the document owner — the SAME role signal
 * DbConnectorsShow reads. Reached from the master list at /admin/documents;
 * task-06 reuses this same source under /me/documents (not a fork).
 */
import { useState } from 'react';
import { usePermissions, useGetIdentity, useShow } from '@refinedev/core';
import { useParams } from 'react-router';
import type { DocumentDetail, TfUserRow } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { hub } from '@/lib/hub-client';
import { isAdminRoles } from '@/lib/require-admin';
import { fmtDateTime } from '@/lib/format';
import { DocumentStatusBadge } from './List';

export const NO_ACCESS_COPY =
  'Document not found — it may have been deleted, or you do not have access to it.';

function YesNo({ value }: { value: boolean }) {
  return (
    <Badge variant={value ? 'success' : 'muted'} className="rounded-full">
      {value ? 'Yes' : 'No'}
    </Badge>
  );
}

/** Pure summary paragraph (exported seam). */
export function SummaryCard({ summary }: { summary: string | null }) {
  return summary ? (
    <p className="whitespace-pre-wrap text-sm">{summary}</p>
  ) : (
    <p className="text-sm text-muted-foreground">No summary generated yet.</p>
  );
}

/** Pure readiness card (exported seam). */
export function ReadinessCard({ readiness }: { readiness: DocumentDetail['readiness'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Readiness</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-28 text-muted-foreground">Status</span>
          <DocumentStatusBadge status={readiness.status} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-28 text-muted-foreground">Ready</span>
          <YesNo value={readiness.ready} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-28 text-muted-foreground">Searchable</span>
          <YesNo value={readiness.searchable} />
        </div>
        {readiness.failureReason && (
          <div className="flex items-start gap-2">
            <span className="w-28 shrink-0 text-muted-foreground">Failure</span>
            <span className="text-destructive">{readiness.failureReason}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Pure access-summary card (exported seam). */
export function AccessCard({ access }: { access: DocumentDetail['access'] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <span className="w-36 shrink-0 text-muted-foreground">Owner</span>
          <code className="font-mono text-xs">{access.ownerUserId ?? '—'}</code>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-36 text-muted-foreground">Shared with track</span>
          <YesNo value={access.sharedWithTrack} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-36 text-muted-foreground">Shared with everyone</span>
          <YesNo value={access.sharedWithEveryone} />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-36 text-muted-foreground">Viewers</span>
          <span className="tabular-nums">{access.viewerCount}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function DocumentsShow() {
  // The SAME admin signal RequireAdmin consumes; plus identity so the owner
  // (uploader) also gets the Generate-summary action when this same component
  // is mounted at /me/documents (task-06).
  const { data: roles } = usePermissions<unknown>({});
  const { data: identity } = useGetIdentity<TfUserRow>();
  const isAdmin = isAdminRoles(roles);

  // Resource + id are passed explicitly so the read works from BOTH shells.
  // Refine can only infer the `documents` resource off the registered
  // /admin/documents/show/:id route; at /me/documents/show/:id (task-06) that
  // segment match fails, leaving the resource undefined and bouncing a /me user
  // to the no-access state. Pinning it keeps admin behavior identical (same
  // resource + `:id`) while making the detail read route-agnostic (D-14).
  const { id } = useParams();
  const { query } = useShow<DocumentDetail>({ resource: 'documents', id });
  const doc = query.data?.data;

  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  if (query.isLoading) return <div>Loading…</div>;
  if (!doc) return <Alert>{NO_ACCESS_COPY}</Alert>;

  const isOwner = identity?.id !== undefined && identity.id === doc.uploaderUserId;
  const canGenerate = isAdmin || isOwner;

  const generateSummary = async () => {
    setSummaryBusy(true);
    setSummaryError(null);
    try {
      await hub.documentsGenerateSummary({ params: { id: doc.id } });
      await query.refetch();
    } catch (err) {
      setSummaryError((err as Error).message);
    } finally {
      setSummaryBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{doc.filename}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <DocumentStatusBadge status={doc.status} />
            <code className="font-mono text-xs">{doc.mimeType}</code>
            <span>· uploaded {fmtDateTime(doc.createdAt)}</span>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Summary</CardTitle>
          {canGenerate && (
            <Button variant="outline" size="sm" onClick={() => void generateSummary()} disabled={summaryBusy}>
              {summaryBusy ? 'Generating…' : doc.summary ? 'Regenerate summary' : 'Generate summary'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {summaryError && <Alert>{summaryError}</Alert>}
          <SummaryCard summary={doc.summary} />
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <ReadinessCard readiness={doc.readiness} />
        <AccessCard access={doc.access} />
      </div>
    </div>
  );
}
